import { useState, useEffect } from 'react';
import { Check, Lock, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@constants';
import PlanService from '@services/api/plan.service';
import { mapPlansToFrontend } from '@utils/planMapper';
import { useAuth } from '@hooks/useAuth';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import RazorpayCheckout from '@components/payment/RazorpayCheckout';

const PricingPlans = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activatingPlanId, setActivatingPlanId] = useState(null);
  const [hasUsedFreePlan, setHasUsedFreePlan] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) {
      fetchUserSubscriptionStatus();
    }
  }, [isAuthenticated]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await PlanService.getAllPlans();
      
      if (response.success && response.data) {
        const mappedPlans = mapPlansToFrontend(response.data);
        setPlans(mappedPlans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubscriptionStatus = async () => {
    try {
      const response = await PlanService.getCurrentPlan();
      if (response.success && response.data) {
        // Check if user has used free plan from backend response
        setHasUsedFreePlan(response.data.hasUsedFreePlan || false);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      // If error (like no subscription), assume false
      setHasUsedFreePlan(false);
    }
  };

  const handlePlanClick = async (planId, planSlug, planPrice, planName) => {
    console.log('🎯 Plan clicked:', { planId, planSlug, planPrice, planName });
    
    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      console.log('⚠️ User not authenticated, redirecting to login');
      navigate(`${ROUTES.LOGIN}?plan=${planId}`);
      return;
    }

    // If free plan already used, show error
    if (planSlug === 'free' && hasUsedFreePlan) {
      console.log('⚠️ Free plan already used');
      toast.error('You have already used the free plan. Please choose a paid plan to continue.', {
        duration: 5000,
        icon: '⚠️'
      });
      return;
    }

    setActivatingPlanId(planId);

    // Free plan - activate directly
    if (planPrice === 0) {
      console.log('💚 Free plan detected, using direct activation');
      await handleDirectActivation(planId);
      return;
    }

    // Paid plan - Razorpay payment flow
    console.log('💰 Paid plan detected, starting Razorpay flow');
    await handleRazorpayPayment(planId, planPrice, planName);
  };

  // PRODUCTION: Razorpay Payment Flow
  const handleRazorpayPayment = async (planId, planPrice, planName) => {
    try {
      console.log('🔵 Starting Razorpay payment flow...', { planId, planPrice, planName });
      
      // Step 1: Create order
      console.log('🔵 Creating Razorpay order...');
      const orderResponse = await PlanService.createPaymentOrder(planId);
      console.log('🔵 Order response:', orderResponse);
      
      if (!orderResponse.success) {
        throw new Error('Failed to create payment order');
      }

      const { orderId, amount, currency } = orderResponse.data;
      console.log('🔵 Order created:', { orderId, amount, currency });

      // Step 2: Open Razorpay checkout
      console.log('🔵 Opening Razorpay checkout...');
      await RazorpayCheckout.initiatePayment({
        orderId,
        amount,
        currency,
        planName,
        onSuccess: async (paymentData) => {
          console.log('✅ Payment successful:', paymentData);
          await handlePaymentSuccess(paymentData);
        },
        onFailure: (error) => {
          console.error('❌ Payment failed:', error);
          handlePaymentFailure(error);
        }
      });
    } catch (error) {
      console.error('❌ Payment initiation error:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      toast.error(error.message || 'Failed to initiate payment. Please try again.');
      setActivatingPlanId(null);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      // Verify payment with backend
      const response = await PlanService.verifyPayment(paymentData);
      
      if (response.success) {
        toast.success(`Payment successful! Plan ${response.data.action} successfully! 🎉`, {
          duration: 5000
        });
        
        // Redirect to dashboard
        setTimeout(() => {
          navigate(ROUTES.STUDENT.CAREER_MAP);
        }, 1500);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Payment verification failed. Please contact support if amount was deducted.');
    } finally {
      setActivatingPlanId(null);
    }
  };

  const handlePaymentFailure = (error) => {
    console.error('Payment failed:', error);
    toast.error(error || 'Payment failed. Please try again.');
    setActivatingPlanId(null);
  };

  // For free plans, use direct activation
  const handleDirectActivation = async (planId) => {
    try {
      const response = await PlanService.activatePlan(planId);
      
      if (response.success) {
        toast.success(`Plan ${response.data.action} successfully! 🎉`);
        
        setTimeout(() => {
          navigate(ROUTES.STUDENT.CAREER_MAP);
        }, 1500);
      }
    } catch (error) {
      console.error('Plan activation error:', error);
      
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || 'Failed to activate plan. Please try again.';
      const errorCode = errorData?.error;
      
      if (errorCode === 'FREE_PLAN_ALREADY_USED' || errorMessage.includes('already used the free plan')) {
        toast.error('You have already used the free plan. Please choose a paid plan to continue.', {
          duration: 5000,
          icon: '⚠️'
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setActivatingPlanId(null);
    }
  };

  // Shared plan-card markup so mobile carousel and the desktop grid render
  // identical cards — only the wrapping layout differs between the two.
  const renderPlanCard = (plan, options = {}) => {
    const { compact = false } = options;

    return (
      <div
        key={plan.id}
        className={`group relative rounded-2xl transition-all duration-300 ease-out flex flex-col bg-white ${
          compact
            ? 'p-4 h-full'
            : 'p-6 md:p-8 hover:bg-[#1a2642] hover:-translate-y-2 hover:shadow-2xl'
        } ${
          plan.popular
            ? `border-2 border-[#1a2642] shadow-xl z-10 ${compact ? '' : 'lg:scale-105 hover:lg:scale-110'}`
            : `border border-gray-200 shadow-lg ${compact ? '' : 'hover:scale-105'}`
        }`}
      >
        {/* Popular Badge */}
        {plan.popular && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#c33c3d] text-white text-sm font-semibold shadow-lg whitespace-nowrap">
              Popular
            </span>
          </div>
        )}

        {/* Plan Name */}
        <h3 className={`font-bold text-gray-900 transition-colors ${compact ? 'text-lg mb-3' : 'text-2xl mb-6 group-hover:text-white'}`}>
          {plan.name}
        </h3>

        {/* Price */}
        <div className={compact ? 'mb-4' : 'mb-6 md:mb-8'}>
          <div className="flex items-baseline gap-1">
            <span className={`font-bold text-[#c33c3d] transition-colors ${compact ? 'text-3xl' : 'text-4xl group-hover:text-white'}`}>
              ₹{plan.price}
            </span>
            <span className={`text-gray-600 transition-colors ${compact ? 'text-base' : 'text-lg group-hover:text-white/80'}`}>
              /{plan.duration}
            </span>
          </div>
          <p className={`text-gray-500 transition-colors mt-1 ${compact ? 'text-xs' : 'text-xs group-hover:text-white/70'}`}>
            then incl. GST
          </p>
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-6 flex-grow">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              {feature.accessible ? (
                <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 text-[#1a2642] transition-colors ${compact ? '' : 'group-hover:text-white'}`} />
              ) : (
                <Lock className={`w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400 transition-colors ${compact ? '' : 'group-hover:text-white/60'}`} />
              )}
              <span className={`text-xs font-semibold transition-colors ${
                feature.accessible
                  ? `text-gray-700 ${compact ? '' : 'group-hover:text-white'}`
                  : `text-gray-400 ${compact ? '' : 'group-hover:text-white/60'}`
              }`}>
                {feature.name}
              </span>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <button
          onClick={() => handlePlanClick(plan.id, plan.slug, plan.price, plan.name)}
          disabled={activatingPlanId === plan.id || (plan.slug === 'free' && hasUsedFreePlan)}
          className={`w-full rounded-lg font-semibold transition-all mt-auto bg-[#1a2642] text-white border-2 border-[#1a2642] hover:bg-[#2a3652] shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
            compact ? 'py-3 text-sm' : 'py-3.5 group-hover:bg-white group-hover:text-[#1a2642] group-hover:border-white'
          }`}
        >
          {activatingPlanId === plan.id ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {plan.price !== 0 ? 'Processing...' : 'Activating...'}
            </>
          ) : plan.slug === 'free' && hasUsedFreePlan ? (
            'Already Used'
          ) : (
            plan.price !== 0 ? 'Get Started' : 'Start Free Trial'
          )}
        </button>
      </div>
    );
  };

  return (
    <section className="py-6 sm:py-10 md:py-16 bg-gradient-to-b from-primary-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-5 sm:mb-8 md:mb-10">
          <p className="text-xs sm:text-sm text-primary-500 mb-1.5 sm:mb-2">Find your plans</p>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-primary-900 mb-2 sm:mb-4">
            Find the <span className="text-secondary-600">Right Plan.</span>
          </h2>
          <p className="text-sm sm:text-lg text-primary-600 max-w-2xl mx-auto">
            Choose the perfect plan to accelerate your career journey with expert guidance and resources.
          </p>
          <div className="inline-flex mt-6 rounded-full bg-primary-100 p-1" role="tablist" aria-label="Billing cycle">
            {[{ value: 'monthly', label: 'Monthly' }, { value: 'weekly', label: 'Weekly' }].map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={billingCycle === tab.value}
                onClick={() => setBillingCycle(tab.value)}
                className={`rounded-full px-6 py-2 text-sm font-semibold transition-colors ${billingCycle === tab.value ? 'bg-primary-900 text-white shadow' : 'text-primary-700 hover:bg-white'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-gray-600">Loading plans...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchPlans}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Plans */}
        {!loading && !error && plans.filter((plan) => plan.billingCycle === billingCycle).length > 0 && (
          <>
            {/* Phone (<640px): horizontal swipeable cards — a vertical stack of
                full-size plan cards means a lot of scrolling to compare plans,
                so on small screens plans become a snap-scrolling carousel instead. */}
            <div className="sm:hidden">
              <div className="-mx-4 px-4 pt-2 overflow-x-auto scrollbar-hide">
                <div className="flex gap-4 snap-x snap-mandatory">
                  {plans.filter((plan) => plan.billingCycle === billingCycle).map((plan) => (
                    <div key={plan.id} className="snap-center shrink-0 w-[78%]">
                      {renderPlanCard(plan, { compact: true })}
                    </div>
                  ))}
                </div>
              </div>
              {plans.filter((plan) => plan.billingCycle === billingCycle).length > 1 && (
                <>
                  <div className="flex items-center justify-center gap-1.5 mt-4">
                    {plans.filter((plan) => plan.billingCycle === billingCycle).map((plan) => (
                      <span key={plan.id} className="w-1.5 h-1.5 rounded-full bg-secondary-300" />
                    ))}
                  </div>
                  <p className="text-center text-xs text-primary-400 mt-2">Swipe to compare plans →</p>
                </>
              )}
            </div>

            {/* Tablet & desktop: full comparison grid */}
            <div className={`hidden sm:grid grid-cols-1 gap-6 max-w-7xl mx-auto pt-4 ${
              plans.filter((plan) => plan.billingCycle === billingCycle).length === 1 ? 'md:grid-cols-1 max-w-md' :
              plans.filter((plan) => plan.billingCycle === billingCycle).length === 2 ? 'md:grid-cols-2 max-w-3xl' :
              plans.filter((plan) => plan.billingCycle === billingCycle).length === 3 ? 'md:grid-cols-2 lg:grid-cols-3 max-w-5xl' :
              'sm:grid-cols-2 lg:grid-cols-4'
            }`}>
              {plans.filter((plan) => plan.billingCycle === billingCycle).map((plan) => renderPlanCard(plan))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PricingPlans;
